import { useEffect, useState } from "react";
import MobileList from "../../ui/MobileList";
import MobileListItem from "../../ui/MobileListItem";
import MobileSkeleton from "../../ui/MobileSkeleton";
import MobileEmpty from "../../ui/MobileEmpty";
import MobileError from "../../ui/MobileError";
import { useAccessData } from "./useAccessData";
import { MobileStaffSheet, MobileGroupSheet } from "./MobileAccessSheets";

const toggleBtn = (active) =>
  `inline-flex min-h-[44px] flex-1 items-center justify-center rounded-lg px-3 text-sm font-medium ${
    active ? "bg-primary text-mgs-bg" : "border border-primary/50 text-laranjinha/80"
  }`;

const staffSubtitle = (member) => {
  const badges = [];
  if (member.roles?.includes("admin")) badges.push("admin");
  if (member.roles?.includes("master")) badges.push("master");
  return `${member.email} — ${badges.join(", ")}`;
};

export default function MobileAccess({ user }) {
  const { staff, groups, loading, error, refresh } = useAccessData(user);
  const [view, setView] = useState("staff");
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [staffSheetOpen, setStaffSheetOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupSheetOpen, setGroupSheetOpen] = useState(false);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleChanged = () => {
    setStaffSheetOpen(false);
    setGroupSheetOpen(false);
    setSelectedStaff(null);
    setSelectedGroup(null);
    refresh();
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-primary/30 bg-mgs-bg px-4 py-2">
        <button type="button" className={toggleBtn(view === "staff")} onClick={() => setView("staff")}>
          Administradores
        </button>
        <button type="button" className={toggleBtn(view === "groups")} onClick={() => setView("groups")}>
          Grupos
        </button>
        {view === "groups" ? (
          <button
            type="button"
            className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-mgs-bg"
            onClick={() => { setSelectedGroup(null); setGroupSheetOpen(true); }}
          >
            Criar
          </button>
        ) : null}
      </div>

      {loading ? (
        <div aria-label="Carregando acessos" role="status">
          <MobileSkeleton lines={5} />
        </div>
      ) : error ? (
        <MobileError message={error.message ?? "Erro ao buscar acessos."} onRetry={refresh} />
      ) : view === "staff" ? (
        staff.length === 0 ? (
          <MobileEmpty title="Nenhum admin encontrado" message="Promova usuários pela versão desktop." />
        ) : (
          <MobileList>
            {staff.map((member) => (
              <MobileListItem
                key={member._id}
                title={member.name}
                subtitle={staffSubtitle(member)}
                onClick={() => { setSelectedStaff(member); setStaffSheetOpen(true); }}
              />
            ))}
          </MobileList>
        )
      ) : groups.length === 0 ? (
        <MobileEmpty title="Nenhum grupo criado" message="Toque em Criar para montar o primeiro grupo." />
      ) : (
        <MobileList>
          {groups.map((group) => (
            <MobileListItem
              key={group._id}
              title={group.name}
              subtitle={`${group.members?.length || 0} membro(s)`}
              onClick={() => { setSelectedGroup(group); setGroupSheetOpen(true); }}
            />
          ))}
        </MobileList>
      )}

      <MobileStaffSheet
        open={staffSheetOpen}
        onClose={() => setStaffSheetOpen(false)}
        staffUser={selectedStaff}
        user={user}
        onChanged={handleChanged}
      />
      <MobileGroupSheet
        open={groupSheetOpen}
        onClose={() => setGroupSheetOpen(false)}
        group={selectedGroup}
        staff={staff}
        user={user}
        onChanged={handleChanged}
      />
    </div>
  );
}
